import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { publicActivePlans } from "../../redux/planSlice";

const Pricing = ({ showActivePlans = true }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const data = useSelector((state) => state.planSlice.plans);
    const [activePlans, setActivePlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const isDark = theme.palette.mode === "dark";
    const textPrimary = isDark ? theme.palette.text.primary : "#2d3748";
    const textSecondary = isDark ? theme.palette.text.secondary : "#4a5568";
    const bgPrimary = isDark ? theme.palette.background.paper : "#fff";
    const bgTableHead = isDark ? theme.palette.background.default : "#f9fafb";
    const borderColor = isDark ? theme.palette.divider : "#e2e8f0";
    const highlightBg = isDark ? theme.palette.primary.light : "#ebf8ff";
    const highlightText = isDark ? theme.palette.primary.contrastText : "#2d3748";

    useEffect(() => {
        (async () => {
            if (!showActivePlans) {
                return;
            }
            setLoadingPlans(true);
            await dispatch(publicActivePlans()).unwrap();
            setLoadingPlans(false);
        })();
    }, [dispatch]);

    useEffect(() => {
        const filteredPlans = data.filter(
            (planItem) => (!planItem.upsell || planItem.upsell === 0) && !planItem.promoCode
        );
        setActivePlans(filteredPlans);
    }, [data]);

    return (
        <div className="container mx-auto max-w-3xl px-4 py-10 md:px-6">
            <h2 style={{ color: textPrimary }} className="text-3xl font-bold mb-6">{t("pricingTitle")}</h2>

            <div className="space-y-4 mb-8">
                <p style={{ color: textSecondary }}><span className="font-bold">{t("freeMembership")}</span> - {t("freeMembershipDesc")}</p>
                <p style={{ color: textSecondary }}><span className="font-bold">{t("silverMembership")}</span> - {t("silverMembershipDesc")}</p>
                <p style={{ color: textSecondary }}><span className="font-bold">{t("goldMembership")}</span> - {t("goldMembershipDesc")}</p>
                <p style={{ color: textSecondary }}><span className="font-bold">{t("guaranteedMembership")}</span> - {t("guaranteedMembershipDesc")}</p>
            </div>

            {showActivePlans && (
                <Box sx={{ mt: 6 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary, mb: 2 }}>
                        {t("plansLabel")}
                    </Typography>
                    {loadingPlans ? (
                        <Typography sx={{ color: textSecondary }}>
                            {t("fetchingPlans")}
                        </Typography>
                    ) : activePlans.length === 0 ? (
                        <Typography sx={{ color: textSecondary }}>
                            {t("noPlans")}
                        </Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {activePlans.map((planItem) => (
                                <Grid item xs={12} sm={6} md={4} key={planItem._id || planItem.description}>
                                    <Paper
                                        sx={{
                                            p: 2.5,
                                            backgroundColor: bgPrimary,
                                            border: `1px solid ${borderColor}`,
                                            borderRadius: 3,
                                            height: "100%",
                                            position: "relative",
                                            overflow: "hidden",
                                            boxShadow: isDark
                                                ? "0 10px 30px rgba(0,0,0,0.35)"
                                                : "0 12px 32px rgba(241, 99, 99, 0.12)",
                                            transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                                            "&:hover": {
                                                transform: "translateY(-6px)",
                                                boxShadow: isDark
                                                    ? "0 16px 40px rgba(0,0,0,0.5)"
                                                    : "0 18px 40px rgba(241, 99, 99, 0.18)",
                                                borderColor: theme.palette.primary.main,
                                            },
                                            "&:before": {
                                                content: '""',
                                                position: "absolute",
                                                top: -40,
                                                right: -40,
                                                width: 120,
                                                height: 120,
                                                borderRadius: "50%",
                                                background: isDark
                                                    ? "radial-gradient(circle, rgba(99,102,241,0.25), rgba(99,102,241,0))"
                                                    : "radial-gradient(circle, rgba(241, 99, 99, 0.18), rgba(241, 99, 99, 0))",
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontWeight: 700,
                                                letterSpacing: 1,
                                            }}
                                        >
                                            {t("plan")}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary, mt: 0.5 }}>
                                            {planItem.description}
                                        </Typography>
                                        <Box
                                            sx={{
                                                mt: 2,
                                                display: "inline-flex",
                                                alignItems: "baseline",
                                                gap: 0.5,
                                                px: 1.25,
                                                py: 0.5,
                                                borderRadius: 999,
                                                background: isDark
                                                    ? "rgba(99,102,241,0.15)"
                                                    : "rgba(241, 99, 99, 0.12)",
                                            }}
                                        >
                                            <Typography variant="h5" fontWeight={800} sx={{ color: textPrimary }}>
                                                {planItem.currencySymbol} {planItem.value}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            )}
        </div>
    );
};

export default Pricing;
